import { useEffect } from "react";
import { CalendarX } from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Banner } from "@/components/Banner";
import { EventHero } from "@/components/event/EventHero";
import { ItemsSection } from "@/components/event/ItemsSection";
import { ParticipantsBar } from "@/components/event/ParticipantsBar";
import { ParticipantsList } from "@/components/event/ParticipantsList";
import { GuestFooter } from "@/components/GuestFooter";
import { Skeleton } from "@/components/Skeleton";
import { useApiErrorToast, useToast } from "@/components/Toast";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  useAddUserItem,
  useClaimItem,
  useReleaseItem,
  useRememberAsUser,
  useSetUserComment,
  useUpdateSelf,
  useUserEvent,
} from "@/hooks/useUserEvent";

export function UserEvent() {
  const params = useParams();
  const shortcut = params.shortcut ?? "";
  const userKey = params.userkey ?? "";
  const { t } = useTranslation();
  const { data: event, isLoading, error } = useUserEvent(shortcut, userKey);
  const remember = useRememberAsUser();
  const updateSelf = useUpdateSelf(shortcut, userKey);
  const claim = useClaimItem(shortcut, userKey);
  const release = useReleaseItem(shortcut, userKey);
  const comment = useSetUserComment(shortcut, userKey);
  const addItem = useAddUserItem(shortcut, userKey);
  const toast = useToast();
  const apiError = useApiErrorToast();

  useEffect(() => {
    if (event) remember(event, userKey);
  }, [event, userKey, remember]);

  usePageMeta({
    title: event?.name ?? t("user.rsvpHeading"),
    robots: "noindex,nofollow",
  });

  if (isLoading) return <Skeleton variant="page" />;
  if (error || !event) {
    const status = (error as { status?: number })?.status;
    if (status === 404 || status === 403) {
      return (
        <section>
          <h1>{t("join.notFoundTitle")}</h1>
          <p>{t("join.notFoundBody")}</p>
        </section>
      );
    }
    return (
      <section>
        <h1>{t("join.errorTitle")}</h1>
        <p>{t("join.errorBody")}</p>
      </section>
    );
  }

  const canceled = event.canceledAt !== null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const guestUrl = `${origin}/view/${event.shortcut}`;
  const windowLimit = event.deadline ?? event.eventAt;
  const windowOpen = new Date(windowLimit).getTime() >= Date.now();
  const canAddItem = event.expandable && !canceled && windowOpen;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}>
      <EventHero event={event} role="user" guestUrl={guestUrl} />

      {canceled && (
        <Banner variant="danger" icon={CalendarX} title={t("user.canceledBanner")}>
          {event.cancelReason && t("user.canceledReason", { reason: event.cancelReason })}
        </Banner>
      )}

      <ParticipantsBar
        participants={event.participants}
        selfId={event.me.id}
        detailsSlot={
          <ParticipantsList
            viewerRole="user"
            participants={event.participants}
            selfId={event.me.id}
            onSaveSelf={(payload) =>
              updateSelf.mutate(payload, {
                onError: (err) => apiError(err, t("common.errorGeneric")),
              })
            }
          />
        }
      />

      <ItemsSection
        viewerRole="user"
        items={event.items}
        participants={event.participants}
        selfId={event.me.id}
        selfName={event.me.name}
        disabled={canceled}
        onClaim={(id) =>
          claim.mutate(id, {
            onSuccess: () => toast.success(t("user.claimedToast")),
            onError: (err) => apiError(err, t("common.errorGeneric")),
          })
        }
        onRelease={(id) =>
          release.mutate(id, {
            onError: (err) => apiError(err, t("common.errorGeneric")),
          })
        }
        onSetUserComment={(id, text) =>
          comment.mutate(
            { itemId: id, comment: text },
            {
              onSuccess: () => toast.success(t("user.commentSavedToast")),
              onError: (err) => apiError(err, t("common.errorGeneric")),
            },
          )
        }
        canAddItem={canAddItem}
        isAddPending={addItem.isPending}
        onAddItem={(payload) =>
          addItem.mutate(payload, {
            onSuccess: () => toast.success(t("user.itemAddedToast")),
            onError: (err) => apiError(err, t("common.errorGeneric")),
          })
        }
      />
      <GuestFooter />
    </section>
  );
}
